import { find } from "lodash";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { SortArrow } from "../icons/SortArrow";
import { Flex } from "./Flex";
import { Label, Paragraph } from "./Text";

const StyledTableContainer = styled.div`
    height: ${({ height}) => height || "100%"};
    // calc(100% - 141px);
    background: #FFFFFF;
    box-shadow: 0px 16px 48px rgba(116, 151, 208, 0.1);
    border-radius: 20px;
    padding: ${({ padding }) => padding ? padding : "32px"};
    box-sizing: border-box;
`;

const TableContainer = (props) => <StyledTableContainer {...props}>{props.children}</StyledTableContainer>;

const StyledColumnTitle = styled.span`
    display: flex;
    align-items: center;
    cursor: pointer;

    & span {
        color: ${({sorted}) => sorted ? "var(--sp-main-color)" : "#8898B2"};
    }

    & span:not(:last-child) {
        margin-right: 4px;
    }
`;

const ColumnTitle = ({ sortOrder, sortColumn, dataIndex, title }) => {
    let sorted = sortColumn && sortColumn === dataIndex && sortOrder;
    return(
        <StyledColumnTitle sorted={sorted}>
            <span>{title}</span>
            {sorted ? <SortArrow size={16} rotate={ sortOrder === "desc" ? 0 : 180 } /> : null}
        </StyledColumnTitle>
    )
};

const StyledTableWrapper = styled.div`
    height: 100%;
    width: 100%;
    min-height: 86px;
    border-bottom: 2px solid #D7E3F7;
`;

const StyledTable = styled.table`
    width: 100%;
    display: grid;
    grid-template-columns: ${({gridTemplateColumns}) => gridTemplateColumns};

    & thead, & tbody, & tr {
        display: contents;
    }

    & th {
        min-height: 40px;
        display: flex;
        align-items: center;
        position: relative;
        text-align: left;
        padding: 0 12px;
        font-weight: 600;
        font-size: 12px;
        line-height: 16px;
        text-transform: uppercase;
        color: #8898B3;
        border-bottom: 2px solid #D7E3F7;
    }

    & tbody > tr:nth-child(even) > td {
        background: #F6F9FF;
    }

    & .table-placeholder {
        width: 100%;
        text-align: center;
    }

    & th > .resize-handler {
        position: absolute;
        top: 50%;
        right: 0;
        width: 1px;
        height: 1.6em;
        background-color: #D7E3F7;
        transform: translateY(-50%);
        transition: background-color .3s;
        cursor: col-resize;
    }
`;

const StyledTableScrollbar = styled.div`
    height: ${({ height }) => height};
    width: 100%;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(139, 164, 206, 0.4);
        border-radius: 10px;
        border: 1px solid transparent;
        background-clip: padding-box;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(139, 164, 206, 0.6);
      border: 1px solid transparent;
      background-clip: padding-box;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
        /* does not work */
    }
`

const Cell = styled.td`
    padding: 13px;
    font-size: 14px;
    line-height: 22px;
    color: var(--sp-text-color);
    border: none;
    display: flex;
    align-items: center;

    overflow: ${({ ellipsis }) => ellipsis && "hidden"};
    text-overflow: ${({ ellipsis }) => ellipsis && "ellipsis"};
    white-space: ${({ ellipsis }) => ellipsis && "nowrap"};
`;

const TableContent = ({ loading, dataSource=[], emptyText, sortData, columns, height="100%" }) => {
    const renderContent = (data) => {
        return data.map((row, idx) => (
            <tr key={`row-${idx}`}>
                {columns.map((col, i) => (
                    <Cell key={`cell-${idx}-${i}`} ellipsis={typeof col.ellipsis === "boolean" ? col.ellipsis : true }>
                        {loading
                            ? <Paragraph isLoading lineHeight="22px" width="100%"/>
                            : col.render
                                ? col.render(row[col.dataIndex], row, i)
                                : row[col.dataIndex]}
                    </Cell>
                ))}
            </tr>
        ))
    };

    const countEmptyRows = () => {
        let rows = 0;
        const container = document.getElementById("table-container");
        if (container && container.clientHeight) {
            rows = Math.ceil(container.clientHeight / 48);
        }
        return rows;
    };

    return(loading || dataSource.length
            ? <StyledTableScrollbar id="table-container" height={height} >
                <StyledTable id="table-content" gridTemplateColumns={columns.map(col => col.width ? col.width + "px" : "1fr").join(" ")}>
                    <tbody>
                        {renderContent(loading ? Array(countEmptyRows()).fill(null) : sortData ? sortData(dataSource) : dataSource)}
                    </tbody>
                </StyledTable>
            </StyledTableScrollbar>
            : <Flex flex={1} justify="center" padding={{ top: "13px" }}>
                <Label
                    bold
                    color="#D7E3F7"
                    size={emptyText && emptyText.size ? emptyText.size : "32px"}
                >
                    {emptyText && emptyText.text ? emptyText.text : "No Data"}
                </Label>
            </Flex>
    )
};

const Table = ({
    columns=[],
    dataSource=[],
    sortOrder,
    sortColumn,
    setSorting,
    loading,
    emptyText,
    total
}) => {
    const initialState = {
        order: null,
        column: null
    }
    const [state, setState] = useState(initialState);

    useEffect(() => {
        let table = document.getElementById("table-container");
        if (table && loading == false) {
            table.scrollTop = 0;
        }
    }, [loading]);

    useEffect(() => {
        if (!setSorting) {
            let defaultSortCol = find(columns, col => col.defaultSortOrder);
            if (defaultSortCol) {
                const sortOptions = { column: defaultSortCol.dataIndex, order: defaultSortCol.defaultSortOrder };
                setState(sortOptions);
            }
        }

        return () => setState(initialState);
    }, []);

    useEffect(() => {
        if (setSorting) {
            setState({
                order: sortOrder,
                column: sortColumn
            });
        };
    }, [ setSorting, sortColumn, sortOrder ]);


    const onTableSort = (key) => {
        let col = find(columns, { dataIndex: key });
        if (col && col.sorter) {
            const sortOrders = [ undefined, "asc", "desc" ];
            let sortIndex = key === state.column ? sortOrders.indexOf(state.order) + 1 : 1;
            if (sortIndex > 2) sortIndex = 0;
            const sortOptions = { order: sortOrders[sortIndex], column: key }
            if (setSorting) {
                setSorting(sortOptions);
            } else {
                setState(sortOptions)
            }
        }
    };

    const sortData = (data) => {
        if (!setSorting) {
            const { column, order } = state;
            let col = find(columns, { dataIndex: column });
            if (col && col.sorter) {
                if (order === "asc") {
                    data = data.slice(0).sort((a,b) => col.sorter(a, b, column));
                } else if (order === "desc") {
                    data = data.slice(0).sort((a,b) => col.sorter(a, b, column)).reverse();
                };
            };
        }
        return data;
    };


    return(
        <StyledTableWrapper>
            <StyledTable id="table-head" gridTemplateColumns={columns.map(col => col.width ? col.width + "px" : "1fr").join(" ")}>
                <thead>
                    <tr>
                        {columns.map((col, idx) => (
                            <th key={`col-header-${idx}`} onClick={() => onTableSort(col.dataIndex)}>
                                {col.sorter
                                    ? <ColumnTitle
                                        sortOrder={state.order}
                                        sortColumn={state.column}
                                        dataIndex={col.dataIndex}
                                        title={col.title}
                                    />
                                    : col.title
                                }
                            </th>
                        ))}
                    </tr>
                </thead>
            </StyledTable>
            <TableContent loading={loading} dataSource={dataSource} emptyText={emptyText} total={total} columns={columns} sortData={sortData} height="calc(100% - 36px)" />
        </StyledTableWrapper>
   )
};

export { Table, TableContent, TableContainer };